import { Space } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'components/atoms/button';
import { Form as FormAtom } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { RadioGroup } from 'components/atoms/radio-buttons';
import {
  BudgetedHoursCalculationMode,
  BudgetedHoursConfig,
} from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import {
  AutomaticLabelWrapper,
  BudgetHoursInputWrapper,
  FooterWrapper,
  Form,
  FormItem,
  Hint,
  ManualBudgetedHoursFormItem,
  Radio,
} from './component';
import { useEstimatedBudgetHours } from './use-estimated-bugdet-hours';

const minimum_budgeted_hours = 1;

interface Props {
  budgetedHoursConfig?: BudgetedHoursConfig;
  save: (budgetHours?: BudgetedHoursConfig) => void;
  cancel: () => void;
  reset: () => void;
}

export const BudgetHoursModal: FC<Props> = ({
  budgetedHoursConfig,
  save,
  reset,
  cancel,
}) => {
  const [form] = FormAtom.useForm();
  const [isDisabled, setDisabled] = useState(false);
  const { estimatedHoursAsText } = useEstimatedBudgetHours();
  const onSave = useCallback(() => {
    const mode = form.getFieldValue('mode');

    save(
      mode === BudgetedHoursCalculationMode.Auto
        ? {
            mode: BudgetedHoursCalculationMode.Auto,
          }
        : {
            mode: BudgetedHoursCalculationMode.Manual,
            value:
              Number(form.getFieldValue('budgetedHours')) === 0
                ? minimum_budgeted_hours
                : form.getFieldValue('budgetedHours'),
          },
    );
  }, [form, save]);
  const placeholderForBudgetHoursInput = useTranslation(
    'templates.budget_hours.placeholder',
  );

  return (
    <Modal
      title={<FormattedMessage id="project_hours_budget" />}
      centered
      footer={
        <FooterWrapper>
          <div>
            <Button type="text" onClick={reset} danger>
              <FormattedMessage id="reset" />
            </Button>
          </div>
          <div>
            <Button type="text" onClick={cancel}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button type="primary" onClick={onSave} disabled={isDisabled}>
              <FormattedMessage id="save" />
            </Button>
          </div>
        </FooterWrapper>
      }
      visible
      onCancel={cancel}
    >
      <Form
        layout="vertical"
        initialValues={{
          budgetedHours:
            budgetedHoursConfig?.mode === BudgetedHoursCalculationMode.Auto
              ? 0
              : budgetedHoursConfig?.value ?? minimum_budgeted_hours,
          mode: budgetedHoursConfig?.mode ?? BudgetedHoursCalculationMode.Auto,
        }}
        form={form}
      >
        <FormItem name="mode">
          <RadioGroup>
            <Space direction="vertical">
              <Radio value={BudgetedHoursCalculationMode.Auto}>
                <AutomaticLabelWrapper>
                  <FormattedMessage id="templates.budget_hours.options.automatic.label" />
                  <Hint>
                    <FormattedMessage
                      id="templates.budget_hours.options.automatic.hint"
                      values={{
                        hours: estimatedHoursAsText,
                        strong: (chunks) => <strong>{chunks}</strong>,
                        br: () => <br />,
                      }}
                    />
                  </Hint>
                </AutomaticLabelWrapper>
              </Radio>
              <Radio value={BudgetedHoursCalculationMode.Manual}>
                <FormattedMessage id="templates.budget_hours.options.manual.label" />
              </Radio>
            </Space>
          </RadioGroup>
        </FormItem>
        <ManualBudgetedHoursFormItem shouldUpdate>
          {() => (
            <BudgetHoursInputWrapper
              hide={
                form.getFieldValue('mode') === BudgetedHoursCalculationMode.Auto
              }
            >
              <InputNumber
                type="number"
                min={1}
                value={form.getFieldValue('budgetedHours')}
                onChange={(e) => {
                  setDisabled(!!(e && e.toString().indexOf('.') !== -1));
                  form.setFieldsValue({
                    budgetedHours: e,
                  });
                }}
                placeholder={placeholderForBudgetHoursInput}
              />
            </BudgetHoursInputWrapper>
          )}
        </ManualBudgetedHoursFormItem>
      </Form>
    </Modal>
  );
};
